/**
 * Settings > Service bundles > Alerts (tracker item 85, ADMIN only): who is
 * alerted about pending renewals, pending portal bundle requests and blocked
 * automatic renewals. A rule is any mix of roles, specific users, everyone who
 * can see staff alerts and the user who created the original enrollment.
 * Only ADMIN and OFFICE_MANAGER users can be chosen.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { createAPIClient, IBundlesApi } from '../../api';
import * as types from '../../api/types';
import { useAuth } from '../../context/AuthContext';
import { canManageBundleAlerts } from '../../utils/permissions';
import { getErrorMessage } from '../../utils/apiError';
import { primaryButtonClass } from '../../components/formStyles';
import { Toast } from '../../components/Toast';
import { BUNDLES_SETTINGS_PATH } from '../../features/service-bundles/bundleUi';

export const ALERT_TYPE_INFO: { type: types.BundleAlertType; title: string; description: string }[] = [
  { type: 'BUNDLE_RENEWAL_PENDING', title: 'Renewal waiting for approval', description: 'A renewal was created (by hand, in bulk or automatically) and waits for approval.' },
  { type: 'BUNDLE_REQUEST_PENDING', title: 'Portal bundle request', description: 'A customer asked for a service bundle from the portal and the request waits for approval.' },
  { type: 'BUNDLE_RENEWAL_BLOCKED', title: 'Automatic renewal blocked', description: 'An automatic renewal could not be created because the bundle is inactive or deleted, or nothing in it can be scheduled.' },
];

const ROLE_LABEL: Record<types.BundleAlertRole, string> = { ADMIN: 'Admins', OFFICE_MANAGER: 'Office managers' };

export function userLabel(user: types.BundleAlertUser): string {
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ');
  return name ? `${name} (${user.email})` : user.email;
}

export function BundleAlertsPage() {
  const { user } = useAuth();
  const api = useMemo(() => createAPIClient() as IBundlesApi, []);
  const allowed = canManageBundleAlerts(user);
  const [saved, setSaved] = useState<types.BundleAlertRoutingResponse | null>(null);
  const [routing, setRouting] = useState<types.BundleAlertRouting | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!allowed) return;
    let active = true;
    api.getBundleAlertRouting()
      .then((result) => { if (active) { setSaved(result); setRouting(result.routing); } })
      .catch((err) => { if (active) setError(getErrorMessage(err, 'Could not load the alert settings')); });
    return () => { active = false; };
  }, [api, allowed]);

  if (!allowed) return <p className="p-4 text-sm text-gray-600">Bundle alerts are set by an admin.</p>;

  const patch = (type: types.BundleAlertType, change: Partial<types.BundleAlertRule>) =>
    setRouting((current) => (current ? { ...current, [type]: { ...current[type], ...change } } : current));
  const toggle = <T,>(list: T[], value: T): T[] => (list.includes(value) ? list.filter((x) => x !== value) : [...list, value]);

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!routing || busy) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await api.updateBundleAlertRouting(routing);
      setSaved(result);
      setRouting(result.routing);
      setNotice('Alert settings saved.');
    } catch (err) {
      setError(getErrorMessage(err, 'Could not save the alert settings'));
    } finally {
      setBusy(false);
    }
  };

  const warningFor = (type: types.BundleAlertType) => saved?.warnings.find((w) => w.type === type);

  return (
    <main className="mx-auto max-w-3xl px-4 py-6 sm:px-6 lg:px-8 sm:py-8">
      <Link to={BUNDLES_SETTINGS_PATH} className="text-sm text-blue-700 hover:underline">← Service bundles</Link>
      <h1 className="mt-2 text-2xl font-bold text-gray-900">Service bundle alerts</h1>
      <p className="mt-1 text-sm text-gray-600">Choose who gets each alert in the notification bell. Only admins and office managers can see the bell, so only they can be chosen. Anyone&apos;s own mutes (Settings &gt; My alerts) still apply. If nobody is left, the alert goes to all admins.</p>
      {error && <Toast kind="error">{error}</Toast>}
      {notice && <Toast kind="success">{notice}</Toast>}
      {!saved || !routing ? (
        !error && <p role="status" className="mt-6 text-sm text-gray-500">Loading...</p>
      ) : (
        <form onSubmit={save} className="mt-6 space-y-6" noValidate>
          {ALERT_TYPE_INFO.map((info) => {
            const rule = routing[info.type];
            const warning = warningFor(info.type);
            return (
              <fieldset key={info.type} className="rounded-lg bg-white p-5 shadow sm:p-6" data-testid={`rule-${info.type}`}>
                <legend className="px-1 text-lg font-semibold text-gray-900">{info.title}</legend>
                <p className="text-sm text-gray-600">{info.description}</p>
                {warning && <p role="note" className="mt-3 rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid={`warning-${info.type}`}>{warning.message}</p>}
                <div className="mt-4 space-y-3">
                  <div>
                    <p className="text-sm font-medium text-gray-800">Roles</p>
                    <div className="mt-1 flex flex-wrap gap-4">
                      {(Object.keys(ROLE_LABEL) as types.BundleAlertRole[]).map((role) => (
                        <label key={role} className="inline-flex items-center gap-2 text-sm">
                          <input type="checkbox" className="h-5 w-5" checked={rule.roles.includes(role)} disabled={busy || rule.everyone} onChange={() => patch(info.type, { roles: toggle(rule.roles, role) })} />
                          {ROLE_LABEL[role]}
                        </label>
                      ))}
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" className="h-5 w-5" checked={rule.everyone} disabled={busy} onChange={(e) => patch(info.type, { everyone: e.target.checked })} />
                    Everyone who can see staff alerts
                  </label>
                  <div>
                    <p className="text-sm font-medium text-gray-800">Specific people</p>
                    <ul className="mt-1 grid grid-cols-1 gap-1 sm:grid-cols-2">
                      {saved.eligibleUsers.map((u) => (
                        <li key={u.id}>
                          <label className="inline-flex items-center gap-2 text-sm">
                            <input type="checkbox" className="h-5 w-5" checked={rule.userIds.includes(u.id)} disabled={busy || rule.everyone} onChange={() => patch(info.type, { userIds: toggle(rule.userIds, u.id) })} />
                            <span className="break-all">{userLabel(u)}</span>
                          </label>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" className="h-5 w-5" checked={rule.enrollmentCreator} disabled={busy} onChange={(e) => patch(info.type, { enrollmentCreator: e.target.checked })} />
                    The person who created the original enrollment (in addition)
                  </label>
                </div>
              </fieldset>
            );
          })}
          <div className="flex justify-end"><button type="submit" className={primaryButtonClass} disabled={busy}>{busy ? 'Saving...' : 'Save alert settings'}</button></div>
        </form>
      )}
    </main>
  );
}
